import { forwardRef, type InputHTMLAttributes } from 'react';
import { controlShell, shellInput } from '@/components/ui/control-styles';
import { cn } from '@/lib/utils';
import { sanitizePhoneTyping } from '@/lib/phone-entry';

type PhoneInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value'> & {
  value: string | null | undefined;
  callingCode: string;
  countryCode?: string | null;
};

function carriesOwnCode(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.startsWith('+') || trimmed.startsWith('00');
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value, callingCode, countryCode, className, disabled, onChange, ...props },
  ref,
) {
  const text = value ?? '';
  const international = carriesOwnCode(text);
  return (
    <div dir="ltr" data-disabled={disabled || undefined} className={cn(controlShell, 'flex h-9 items-center overflow-hidden', className)}>
      {callingCode && (
        <span
          aria-hidden
          className={cn(
            'flex h-full shrink-0 items-center gap-1 border-e border-input bg-muted/50 px-2.5 text-xs font-medium tabular-nums text-muted-foreground transition-opacity',
            international && 'opacity-40',
          )}
        >
          {countryCode && <span className="text-[0.65rem] font-semibold tracking-wide text-muted-foreground/80">{countryCode}</span>}
          <span>+{callingCode}</span>
        </span>
      )}
      <input
        ref={ref}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        dir="ltr"
        value={text}
        disabled={disabled}
        className={cn(shellInput, 'pe-3 tabular-nums')}
        {...props}
        onChange={(event) => {
          const cleaned = sanitizePhoneTyping(event.target.value);
          if (cleaned !== event.target.value) event.target.value = cleaned;
          onChange?.(event);
        }}
      />
    </div>
  );
});
